// Stage-1 Two-Stage Billing (兩段式計費) — procurement quote helper.
// MUST stay in sync with the backend engine at
// backend/app/modules/orders/service.py::OrderService._compute_stage1
//
// Stage 1 procurement quote for an order:
//   1. net_items_total  = Σ over items of (price × qty)
//   2. reward_rebate    = 8% of net (退稅紅利), deducted at quote time
//   3. buyer_fee        = per line item: £10 if price >= £100 else £5, × qty
//   4. procurement_subtotal = net − rebate + buyer_fee
//   5. processing_fee   = 3% of subtotal if card, else 0
//   6. payment_total    = subtotal + processing_fee  (= the amount paid)

export const STAGE1_BUYER_FEE_THRESHOLD = 100;
export const STAGE1_BUYER_FEE_HIGH = 10;
export const STAGE1_BUYER_FEE_LOW = 5;
export const STAGE1_REWARD_REBATE_RATE = 0.08;
export const STAGE1_CARD_PROCESSING_RATE = 0.03;

export type Stage1PaymentMethod = 'card' | 'bank_transfer' | 'reward';

export interface Stage1ItemInput {
  price: number;
  quantity: number;
}

export interface Stage1Quote {
  netItemsTotal: number;
  buyerFee: number;
  rewardRebate: number;
  procurementSubtotal: number;
  processingFee: number;
  paymentTotal: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function calculateStage1Quote(
  items: Stage1ItemInput[],
  paymentMethod: Stage1PaymentMethod,
): Stage1Quote {
  let net = 0;
  let buyerFee = 0;
  for (const it of items) {
    const qty = it.quantity || 1;
    const price = it.price || 0;
    net += price * qty;
    buyerFee += (price >= STAGE1_BUYER_FEE_THRESHOLD ? STAGE1_BUYER_FEE_HIGH : STAGE1_BUYER_FEE_LOW) * qty;
  }
  net = round2(net);
  const rebate = round2(net * STAGE1_REWARD_REBATE_RATE);
  buyerFee = round2(buyerFee);
  const subtotal = round2(net - rebate + buyerFee);
  const procFee = paymentMethod === 'card' ? round2(subtotal * STAGE1_CARD_PROCESSING_RATE) : 0;
  const paymentTotal = round2(subtotal + procFee);
  return {
    netItemsTotal: net,
    buyerFee,
    rewardRebate: rebate,
    procurementSubtotal: subtotal,
    processingFee: procFee,
    paymentTotal,
  };
}
